import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ProfilePlaceholder from 'assets/img/profile_placeholder.png';
import { useUploadMedia } from 'hooks/useUploadMedia';
import { Form, ScrollableTabs } from 'components';
import ProductCustomerPreview from 'components/CustomerItemPreview/ProductCustomerPreview';
import { MediaUpload } from 'components/Form/inputs/MediaUpload/MediaUpload';
import ModalForm from 'components/ModalForm/ModalForm';
import {
  useCreateChildProduct,
  useFetchParentProductsInfinite,
  useFetchProductColorsInfinite,
  useFetchProductInventoriesInfinite,
  useFetchProductSizesInfinite,
  useUpdateChildProduct,
} from 'api/products/hooks';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { useAuthStore } from 'stores/authStore';
import { useFetchRegions } from 'api/regions/hooks';
import { mapInitialValues } from '../../utils/childProductFormUtils';
import validationSchemas from './schemas';
import * as Ui from './childProductForm.styles';

const ChildProductForm = ({ type = 'create', initialValues, onRequiredDataLoad }) => {
  const { t } = useTranslation(['pages', 'app']);
  const [workspaceView, setWorkspaceView] = useState('details');
  const [previewImage, setPreviewImage] = useState(null);

  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const user = useAuthStore((state) => state.user);
  const { data: regions } = useFetchRegions();
  const currency = regions.find((region) => region.uid === selectedRegion)?.base_currency;
  const originImage = user?.admin_details?.brand?.theme?.logo || ProfilePlaceholder;

  const [parentProductsSearch, setParentProductsSearch] = useState('');
  const [inventoriesSearch, setInventoriesSearch] = useState('');
  const [colorsSearch, setColorsSearch] = useState('');
  const [sizesSearch, setSizesSearch] = useState('');

  const {
    data: parentProductsData,
    fetchNextPage: fetchMoreParentProducts,
    hasNextPage: hasMoreParentProducts,
    isLoading: isLoadingParentProducts,
    isFetchingNextPage: isFetchingMoreParentProducts,
  } = useFetchParentProductsInfinite({
    pageSize: 30,
    search: parentProductsSearch,
    filters: {
      region: selectedRegion,
    },
  });

  const {
    data: inventoriesData,
    fetchNextPage: fetchMoreInventories,
    hasNextPage: hasMoreInventories,
    isLoading: isLoadingInventories,
    isFetchingNextPage: isFetchingMoreInventories,
  } = useFetchProductInventoriesInfinite({
    pageSize: 30,
    search: inventoriesSearch,
    filters: {
      region: selectedRegion,
    },
    ordering: 'name',
  });

  const {
    data: colorsData,
    fetchNextPage: fetchMoreColors,
    hasNextPage: hasMoreColors,
    isLoading: isLoadingColors,
    isFetchingNextPage: isFetchingMoreColors,
  } = useFetchProductColorsInfinite({
    pageSize: 30,
    search: colorsSearch,
    filters: {
      region: selectedRegion,
    },
    ordering: 'name',
  });

  const {
    data: sizesData,
    fetchNextPage: fetchMoreSizes,
    hasNextPage: hasMoreSizes,
    isLoading: isLoadingSizes,
    isFetchingNextPage: isFetchingMoreSizes,
  } = useFetchProductSizesInfinite({
    pageSize: 30,
    search: sizesSearch,
    filters: {
      region: selectedRegion,
    },
    ordering: 'name',
  });

  const { mutate: createChildProduct, isPending: isCreating } =
    useCreateChildProduct(selectedRegion);
  const { mutate: updateChildProduct, isPending: isUpdating } = useUpdateChildProduct();
  const { mutateAsync: uploadMediaAsync, isPending: isUploading } = useUploadMedia();

  const parentProducts = parentProductsData?.pages.flatMap((page) => page.results) || [];
  const inventories = inventoriesData?.pages.flatMap((page) => page.results) || [];
  const colors = colorsData?.pages.flatMap((page) => page.results) || [];
  const sizes = sizesData?.pages.flatMap((page) => page.results) || [];

  const isSubmitting = isUploading || (type === 'update' ? isUpdating : isCreating);

  const mappedInitialValues = useMemo(
    () => mapInitialValues(initialValues, currency),
    [initialValues, currency],
  );

  const isLoadingAny =
    isLoadingParentProducts || isLoadingInventories || isLoadingColors || isLoadingSizes;

  useEffect(() => {
    if (onRequiredDataLoad) {
      if (!isLoadingAny) {
        const missingItems = [];
        if (parentProducts.length === 0) missingItems.push('parentProduct');
        if (inventories.length === 0) missingItems.push('inventory');
        if (colors.length === 0) missingItems.push('color');
        if (sizes.length === 0) missingItems.push('size');
        onRequiredDataLoad(missingItems);
      }
    }
  }, [
    parentProducts.length,
    inventories.length,
    colors.length,
    sizes.length,
    isLoadingAny,
    onRequiredDataLoad,
  ]);

  return (
    <ModalForm
      initialValues={mappedInitialValues}
      onSubmit={async ({
        uid,
        product_code,
        description,
        thumbnail,
        parent_product_uid,
        inventory_uid,
        color_uid,
        size_uid,
        price,
      }) => {
        let public_id;

        if (thumbnail) {
          try {
            const result = await uploadMediaAsync({ media: thumbnail });
            public_id = result.public_id;
          } catch (error) {
            return;
          }
        }

        const basePayload = {
          product_code,
          description,
          picture_id: public_id,
          parent_product_uid,
          inventory_uid,
          price,
        };

        if (color_uid) basePayload.color_uid = color_uid;
        if (size_uid) basePayload.size_uid = size_uid;

        if (type === 'update') {
          updateChildProduct({ uid, ...basePayload });
        } else {
          createChildProduct(basePayload);
        }
      }}
      validationSchema={validationSchemas.child_product}
      cancelLabel={t('app:cancel')}
      submitLabel={t(`app:${type}`)}
      pristine
      isLoading={isSubmitting}
      ignoreClickedState
      isClosePopupOn={false}
      header={
        <Ui.SwitcherRow>
          <ScrollableTabs
            tabs={[
              { key: 'details', label: t('app:mediaCrop.detailsLabel') },
              { key: 'preview', label: t('app:preview') },
            ]}
            activeTab={workspaceView}
            onTabChange={setWorkspaceView}
            align="center"
            stretch
          />
        </Ui.SwitcherRow>
      }
    >
      {({ values }) => {
        const selectedParentProduct =
          parentProducts.find((product) => product.uid === values.parent_product_uid) ||
          mappedInitialValues.parent_product;
        const selectedColor =
          colors.find((color) => color.uid === values.color_uid) || mappedInitialValues.color;
        const selectedSize =
          sizes.find((size) => size.uid === values.size_uid) || mappedInitialValues.size;

        return (
          <Ui.Workspace>
            <Ui.FormPanel $active={workspaceView === 'details'}>
              <Form.GroupVertical required label={t('createProduct.productCode')}>
                <Form.Input name="product_code" type="text" />
              </Form.GroupVertical>
              <Form.GroupVertical required label={t('createProduct.description')}>
                <Form.MarkdownEditor name="description" maxLength={5000} height={200} />
              </Form.GroupVertical>
              <Form.GroupVertical required label={t('createProduct.price')}>
                <Form.Input
                  style={{ paddingRight: '12px' }}
                  name="price"
                  type="number"
                  min="0"
                  append={initialValues?.currency || currency}
                />
              </Form.GroupVertical>
              <Form.GroupVertical required label={t('createProduct.images')}>
                <MediaUpload
                  accept={{
                    'image/png': ['.png'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                  }}
                  name="thumbnail"
                  maxFiles={1}
                  withCrop
                  cropMediaType="product"
                  setFile={setPreviewImage}
                  previewData={{
                    color: selectedColor,
                    currency: values.currency || initialValues?.currency || currency,
                    description: values.description,
                    name: selectedParentProduct?.name,
                    originImage,
                    price: values.price,
                    size: selectedSize,
                  }}
                />
              </Form.GroupVertical>
              <Form.GroupVertical required label={t('createProduct.parentProduct')}>
                <Form.MultiSelectInfinite
                  options={parentProducts.map((parentProduct) => ({
                    value: parentProduct.uid,
                    label: parentProduct.name.toUpperCase(),
                  }))}
                  initialOption={
                    mappedInitialValues.parent_product && {
                      value: mappedInitialValues.parent_product.uid,
                      label: mappedInitialValues.parent_product.name.toUpperCase(),
                    }
                  }
                  hasMoreOptions={hasMoreParentProducts}
                  loadMoreOptions={fetchMoreParentProducts}
                  isLoadingOptions={isLoadingParentProducts}
                  isLoadingMoreOptions={isFetchingMoreParentProducts}
                  onSearch={setParentProductsSearch}
                  name="parent_product_uid"
                  isMulti={false}
                />
              </Form.GroupVertical>
              <Form.GroupVertical required label={t('createProduct.inventory')}>
                <Form.MultiSelectInfinite
                  options={inventories.map((inventory) => ({
                    value: inventory.uid,
                    label: inventory.name.toUpperCase(),
                  }))}
                  initialOption={
                    mappedInitialValues.inventory && {
                      value: mappedInitialValues.inventory.uid,
                      label: mappedInitialValues.inventory.name.toUpperCase(),
                    }
                  }
                  hasMoreOptions={hasMoreInventories}
                  loadMoreOptions={fetchMoreInventories}
                  isLoadingOptions={isLoadingInventories}
                  isLoadingMoreOptions={isFetchingMoreInventories}
                  onSearch={setInventoriesSearch}
                  name="inventory_uid"
                  isMulti={false}
                />
              </Form.GroupVertical>
              <Form.GroupHorizontal style={{ borderBottom: '1px solid #e9ecef' }}>
                <Form.Label style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {t('createProduct.chooseAtLeastOne')}
                </Form.Label>
              </Form.GroupHorizontal>

              <Form.GroupVertical label={t('createProduct.color')}>
                <Form.MultiSelectInfinite
                  options={colors.map((color) => ({
                    value: color.uid,
                    label: color.name.toUpperCase(),
                  }))}
                  initialOption={
                    mappedInitialValues.color && {
                      value: mappedInitialValues.color.uid,
                      label: mappedInitialValues.color.name.toUpperCase(),
                    }
                  }
                  hasMoreOptions={hasMoreColors}
                  loadMoreOptions={fetchMoreColors}
                  isLoadingOptions={isLoadingColors}
                  isLoadingMoreOptions={isFetchingMoreColors}
                  onSearch={setColorsSearch}
                  name="color_uid"
                  isMulti={false}
                />
              </Form.GroupVertical>
              <Form.GroupVertical label={t('createProduct.size')}>
                <Form.MultiSelectInfinite
                  options={sizes.map((size) => ({
                    value: size.uid,
                    label: size.name.toUpperCase(),
                  }))}
                  initialOption={
                    mappedInitialValues.size && {
                      value: mappedInitialValues.size.uid,
                      label: mappedInitialValues.size.name.toUpperCase(),
                    }
                  }
                  hasMoreOptions={hasMoreSizes}
                  loadMoreOptions={fetchMoreSizes}
                  isLoadingOptions={isLoadingSizes}
                  isLoadingMoreOptions={isFetchingMoreSizes}
                  onSearch={setSizesSearch}
                  name="size_uid"
                  isMulti={false}
                />
              </Form.GroupVertical>
            </Ui.FormPanel>
            <Ui.PreviewPanel $active={workspaceView === 'preview'}>
              <ProductCustomerPreview
                color={selectedColor}
                currency={values.currency || initialValues?.currency || currency}
                description={values.description}
                media={previewImage}
                name={selectedParentProduct?.name}
                originImage={originImage}
                price={values.price}
                size={selectedSize}
              />
            </Ui.PreviewPanel>
          </Ui.Workspace>
        );
      }}
    </ModalForm>
  );
};

ChildProductForm.propTypes = {
  type: PropTypes.oneOf(['create', 'update']),
  initialValues: PropTypes.object,
  onRequiredDataLoad: PropTypes.func,
};

export default ChildProductForm;
